import React,{useMemo,useState} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useSupabaseData} from '../contexts/SupabaseDataContext';
import {useSupabaseAuth} from '../contexts/SupabaseAuthContext';
import {getShopUrl} from '../services/shopLinkService';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from '../components/common/ImageWithFallback';
import {detectCity} from '../services/locationService';
import * as FiIcons from 'react-icons/fi';
import * as GiIcons from 'react-icons/gi';

const {
  FiSearch,
  FiMapPin,
  FiStar,
  FiTarget,
  FiLoader,
  FiArrowRight,
  FiGrid,
  FiList,
  FiAward,
  FiFilter,
  FiX
}=FiIcons;
const {GiChefToque}=GiIcons;

const normalizeAllergens=(allergens)=> {
  if (Array.isArray(allergens)) {
    return allergens
      .map((allergen)=>String(allergen || '').trim().toLowerCase())
      .filter(Boolean);
  }

  return String(allergens || '')
    .split(',')
    .map((allergen)=>allergen.trim().toLowerCase())
    .filter(Boolean);
};

const formatAllergenName=(allergen)=> (
  allergen
    .split(/\s+/)
    .map((word)=>word.charAt(0).toUpperCase()+word.slice(1))
    .join(' ')
);

const SellersPage=()=> {
  const {user}=useSupabaseAuth();
  const {sellers,categories,offerings,reviews,isDataReady}=useSupabaseData();
  const [searchTerm,setSearchTerm]=useState('');
  const [selectedCategory,setSelectedCategory]=useState('All');
  const [selectedLocation,setSelectedLocation]=useState('');
  const [selectedAllergens,setSelectedAllergens]=useState([]);
  const [locLoading,setLocLoading]=useState(false);
  const [viewMode,setViewMode]=useState('grid');

  const uniqueLocations=[
    ...new Set(sellers.map(s=> s.location || s.service_area).filter(Boolean))
  ];

  const dietaryOptions=useMemo(()=> {
    const values=new Set();

    offerings.forEach((offering)=> {
      normalizeAllergens(offering.allergens).forEach((allergen)=> {
        values.add(allergen);
      });
    });

    return [...values].sort();
  },[offerings]);

  const sellerRatings=useMemo(()=>{
    return reviews.reduce((ratings,review)=>{
      const sellerId=String(review.seller_id || '');

      if (!sellerId) return ratings;

      if (!ratings[sellerId]) {
        ratings[sellerId]={total:0,count:0};
      }

      ratings[sellerId].total+=Number(review.rating || 0);
      ratings[sellerId].count+=1;
      return ratings;
    },{});
  },[reviews]);

  const getSellerReviewSummary=(seller)=>{
    const reviewSummary=sellerRatings[String(seller.id)];

    if (reviewSummary?.count) {
      return reviewSummary;
    }

    return {
      total:0,
      count:0
    };
  };

  const getSellerRating=(seller)=>{
    const reviewSummary=getSellerReviewSummary(seller);

    if (!reviewSummary.count) {
      return 0;
    }

    return reviewSummary.total/reviewSummary.count;
  };

  const handleDetectLocation=async()=> {
    setLocLoading(true);
    try {
      const locationData=await detectCity();
      const cityMatch=uniqueLocations.find(loc=> (
        loc.toLowerCase().includes(locationData.city.toLowerCase())
      ));
      setSelectedLocation(cityMatch || '');
      if (!cityMatch) setSearchTerm(locationData.city);
    } catch (error) {
      console.error(error);
    } finally {
      setLocLoading(false);
    }
  };

  const toggleAllergen=(allergen)=>{
    setSelectedAllergens((current)=> (
      current.includes(allergen)
        ? current.filter((value)=>value!==allergen)
        : [...current,allergen]
    ));
  };

  const clearDietaryFilters=()=>{
    setSelectedAllergens([]);
  };

  const filteredSellers=sellers.filter(seller=> {
    const sellerOfferings=offerings.filter(
      offering=>offering.seller_id===seller.id
    );
    const productSearchText=sellerOfferings.map(offering=> [
      offering.title,
      offering.description,
      offering.category
    ].filter(Boolean).join(' ')).join(' ');
    const matchesSearch=seller.business_name?.toLowerCase().includes(
      searchTerm.toLowerCase()
    ) || seller.description?.toLowerCase().includes(searchTerm.toLowerCase())
      || productSearchText.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory=selectedCategory==='All' ||
      seller.category===selectedCategory ||
      sellerOfferings.some(offering=>(
        offering.category===selectedCategory
      ));
    const sellerLoc=seller.location || seller.service_area || '';
    const matchesLocation=!selectedLocation ||
      selectedLocation==='All' ||
      sellerLoc.includes(selectedLocation);
    const matchesDietary=selectedAllergens.length===0
      || sellerOfferings.some((offering)=> {
        const offeringAllergens=normalizeAllergens(offering.allergens);

        return selectedAllergens.every(
          (allergen)=>!offeringAllergens.includes(allergen)
        );
      });

    return matchesSearch
      && matchesCategory
      && matchesLocation
      && matchesDietary;
  });

  if (!isDataReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="text-center">
          <SafeIcon
            icon={FiLoader}
            className="mx-auto mb-5 text-4xl text-indigo-600 animate-spin"
          />
          <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            Loading marketplace...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-4 tracking-tighter leading-none">
              The <span className="text-indigo-600">Marketplace</span>
            </h1>
            <p className="text-slate-500 font-medium text-lg">
              Discover the best home creators in your community.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm flex items-center">
              <button
                onClick={()=> setViewMode('grid')}
                className={`p-2.5 rounded-xl transition-all ${
                  viewMode==='grid'
                    ? 'bg-indigo-600 text-white shadow-lg'
                    : 'text-slate-400 hover:bg-slate-50'
                }`}
              >
                <SafeIcon icon={FiGrid} />
              </button>
              <button
                onClick={()=> setViewMode('list')}
                className={`p-2.5 rounded-xl transition-all ${
                  viewMode==='list'
                    ? 'bg-indigo-600 text-white shadow-lg'
                    : 'text-slate-400 hover:bg-slate-50'
                }`}
              >
                <SafeIcon icon={FiList} />
              </button>
            </div>

            <button
              onClick={handleDetectLocation}
              disabled={locLoading}
              className="flex items-center px-6 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black text-indigo-600 hover:bg-slate-50 transition-all shadow-sm uppercase tracking-widest"
            >
              <SafeIcon
                icon={locLoading ? FiLoader : FiTarget}
                className={`mr-2.5 ${locLoading ? 'animate-spin' : ''}`}
              />
              {locLoading ? 'Detecting...' : 'Near Me'}
            </button>
          </div>
        </div>

        <div className="mb-12 space-y-8">
          <div className="relative w-full">
            <FiSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 text-xl" />
            <input
              type="text"
              placeholder="Search for treats or bakers..."
              value={searchTerm}
              onChange={e=> setSearchTerm(e.target.value)}
              className="w-full pl-16 pr-6 py-6 bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/40 focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-xl placeholder-slate-300 transition-all"
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex overflow-x-auto no-scrollbar gap-3 pb-2 flex-1">
              <button
                onClick={()=> setSelectedCategory('All')}
                className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap shadow-sm border ${
                  selectedCategory==='All'
                    ? 'bg-slate-900 text-white border-slate-900 shadow-lg'
                    : 'bg-white text-slate-400 hover:bg-slate-50 border-slate-100'
                }`}
              >
                All Creations
              </button>

              {categories.map(cat=> (
                <button
                  key={cat.id}
                  onClick={()=> setSelectedCategory(cat.name)}
                  className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap shadow-sm border ${
                    selectedCategory===cat.name
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg'
                      : 'bg-white text-slate-400 hover:bg-slate-50 border-slate-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {dietaryOptions.length>0 && (
            <div className="rounded-[2rem] border border-amber-100 bg-amber-50/60 p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center text-amber-900">
                    <SafeIcon icon={FiFilter} className="mr-2" />
                    <h2 className="text-xs font-black uppercase tracking-[0.2em]">
                      Dietary filters
                    </h2>
                  </div>
                  <p className="mt-2 text-sm font-medium text-amber-800">
                    Hide menu items containing selected allergens.
                  </p>
                </div>

                {selectedAllergens.length>0 && (
                  <button
                    type="button"
                    onClick={clearDietaryFilters}
                    className="inline-flex items-center text-[10px] font-black uppercase tracking-widest text-amber-800 hover:text-amber-950"
                  >
                    <SafeIcon icon={FiX} className="mr-2" />
                    Clear filters
                  </button>
                )}
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                {dietaryOptions.map((allergen)=> {
                  const isSelected=selectedAllergens.includes(allergen);

                  return (
                    <button
                      key={allergen}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={()=>toggleAllergen(allergen)}
                      className={`rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                        isSelected
                          ? 'border-amber-700 bg-amber-700 text-white shadow-md'
                          : 'border-amber-200 bg-white text-amber-800 hover:border-amber-400'
                      }`}
                    >
                      {isSelected ? 'Exclude ' : 'No '}
                      {formatAllergenName(allergen)}
                    </button>
                  );
                })}
              </div>

              {selectedAllergens.length>0 && (
                <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-amber-700">
                  Showing sellers with at least one menu item matching your filters.
                </p>
              )}
            </div>
          )}
        </div>

        <motion.div
          layout
          className={
            viewMode==='grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10'
              : 'flex flex-col gap-6'
          }
        >
          {filteredSellers.map((seller,index)=> {
            const isVerified=seller.policy_settings?.paymentMode==='platform';
            const sellerUrl=seller.shop_username
              ? `/s/${encodeURIComponent(seller.shop_username)}`
              : `/seller/${seller.id}`;
            const reviewSummary=getSellerReviewSummary(seller);

            return (
              <motion.div
                key={seller.id}
                layout
                initial={{opacity:0,y:20}}
                animate={{opacity:1,y:0}}
                transition={{delay:index * 0.05}}
              >
                <Link to={sellerUrl} className="block h-full">
                  <SellerCard
                    seller={seller}
                    rating={getSellerRating(seller)}
                    reviewCount={reviewSummary.count}
                    isVerified={isVerified}
                    viewMode={viewMode}
                  />
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {filteredSellers.length===0 && (
          <div className="py-32 text-center">
            <div className="w-20 h-20 bg-white rounded-3xl border border-slate-100 flex items-center justify-center mx-auto mb-6 shadow-sm">
              <SafeIcon icon={FiSearch} className="text-slate-200 text-3xl" />
            </div>
            <h3 className="text-2xl font-black text-slate-900">
              No creators found
            </h3>
            <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mt-2">
              Try adjusting your filters or search term
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const SellerCard=({seller,rating,reviewCount,isVerified,viewMode})=> (
  <div className={`bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/40 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 overflow-hidden border border-slate-100 group flex flex-col h-full ${
    viewMode==='list' ? 'md:flex-row' : ''
  }`}>
    <div className={`relative flex-shrink-0 ${
      viewMode==='list'
        ? 'w-full h-64 md:w-80 md:h-64'
        : 'w-full h-64'
    }`}>
      <ImageWithFallback
        src={seller.profile_image || seller.profile_images?.[0]}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        alt={seller.business_name}
        fallbackLabel="No seller photo"
        fallbackIcon={GiChefToque}
      />
      <div className="absolute top-6 left-6 flex flex-col gap-2">
        <span className="px-4 py-2 bg-white/90 backdrop-blur-md text-indigo-700 text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg">
          {seller.category}
        </span>
        {isVerified && (
          <span className="px-4 py-2 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg flex items-center">
            <SafeIcon icon={FiAward} className="mr-2" />
            Verified
          </span>
        )}
      </div>
    </div>

    <div className="p-8 flex-1 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-black text-slate-900 group-hover:text-indigo-600 transition-colors tracking-tight">
          {seller.business_name}
        </h3>
        <div className="flex items-center px-3 py-1.5 bg-indigo-50 rounded-xl">
          <SafeIcon icon={FiStar} className="text-indigo-600 fill-indigo-600 text-xs" />
          <span className="text-xs font-black text-indigo-700 ml-1.5">
            {rating>0 ? rating.toFixed(1) : 'New'}
          </span>
          <span className="ml-1 text-[10px] font-bold text-indigo-500">
            ({reviewCount})
          </span>
        </div>
      </div>

      <p className="text-slate-500 text-sm mb-8 line-clamp-2 leading-relaxed font-medium">
        {seller.description}
      </p>

      <div className="mt-auto pt-6 border-t border-slate-50 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center text-slate-400 text-[10px] font-black uppercase tracking-widest">
            <SafeIcon icon={FiMapPin} className="mr-2 text-indigo-500" />
            <span>{seller.location || 'Community Baker'}</span>
          </div>
        </div>
        <div className="flex items-center text-indigo-600">
          <span className="text-sm font-black uppercase tracking-widest mr-2">
            View
          </span>
          <SafeIcon icon={FiArrowRight} />
        </div>
      </div>
    </div>
  </div>
);

export default SellersPage;